import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

export type NotificationSource = 'push' | 'local' | 'server';

export type AppNotification = {
  id: string;
  title: string;
  body: string;
  createdAt: number;
  isRead: boolean;
  source: NotificationSource;
  data?: Record<string, any>;
  /**
   * Location the notice belongs to. Items saved before notices were tagged
   * have none and show at every location until cleared.
   */
  locationId?: string | null;
  type?: string;
};

export type NotificationsSliceObject = {
  items: AppNotification[];
  /**
   * Notices already shown in the inbox. They stop counting on the bell but
   * stay unread until opened (seen ≠ read).
   */
  seenIds: string[];
};

export type AddNotificationPayload = {
  id?: string;
  title?: string;
  body?: string;
  source?: NotificationSource;
  createdAt?: number;
  data?: Record<string, any>;
  locationId?: string | null;
  type?: string;
  isRead?: boolean;
};

/** Actions scoped to one location; without one they apply to every item. */
export type LocationScopePayload = { locationId?: string | null } | undefined;

const initialState: NotificationsSliceObject = {
  items: [],
  seenIds: [],
};

/** A notice shows at its own location; an untagged one at every location. */
export const isNotificationVisibleAt = (
  item: Pick<AppNotification, 'locationId'>,
  locationId: string | null | undefined,
): boolean =>
  !item.locationId ||
  !locationId ||
  String(item.locationId) === String(locationId);

const inScope = (item: AppNotification, scope: LocationScopePayload) =>
  scope?.locationId === undefined
    ? true
    : isNotificationVisibleAt(item, scope.locationId);

const byNewest = (a: AppNotification, b: AppNotification) =>
  b.createdAt - a.createdAt;

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /**
     * Adds a notice, or refreshes one already listed under the same id (a
     * push and the server copy of the same notice share the server id).
     */
    addNotification: (state, action: PayloadAction<AddNotificationPayload>) => {
      const payload = action.payload || {};
      const createdAt = payload.createdAt || Date.now();
      const id =
        payload.id || `${createdAt}-${Math.random().toString(36).slice(2, 10)}`;
      const existing = state.items.find(item => item.id === id);
      const next: AppNotification = {
        id,
        title: payload.title?.trim() || 'Notification',
        body: payload.body?.trim() || '',
        createdAt: existing?.createdAt ?? createdAt,
        isRead: existing?.isRead ?? Boolean(payload.isRead),
        source: payload.source || 'local',
        data: payload.data ?? existing?.data,
        locationId: payload.locationId
          ? String(payload.locationId)
          : (existing?.locationId ?? null),
        type: payload.type ?? existing?.type,
      };
      if (existing) {
        Object.assign(existing, next);
      } else {
        state.items.unshift(next);
      }
    },
    /**
     * The server's list for one location replaces the server items held for
     * it; local notices and other locations' items are kept.
     */
    replaceServerNotifications: (
      state,
      action: PayloadAction<{ locationId: string; items: AppNotification[] }>,
    ) => {
      const locationId = String(action.payload.locationId);
      const incoming = action.payload.items.map(item => ({
        ...item,
        source: 'server' as const,
        locationId,
      }));
      const incomingIds = new Set(incoming.map(item => item.id));
      state.items = state.items.filter(
        item =>
          !incomingIds.has(item.id) &&
          !(item.source === 'server' && String(item.locationId) === locationId),
      );
      state.items.push(...incoming);
      state.items.sort(byNewest);
    },
    markNotificationRead: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const target = state.items.find(item => item.id === id);
      if (target) target.isRead = true;
    },
    setNotificationReadStatus: (
      state,
      action: PayloadAction<{ id: string; isRead: boolean }>,
    ) => {
      const { id, isRead } = action.payload;
      const target = state.items.find(item => item.id === id);
      if (target) target.isRead = isRead;
    },
    markAllNotificationsRead: (
      state,
      action: PayloadAction<LocationScopePayload>,
    ) => {
      state.items = state.items.map(item =>
        inScope(item, action.payload) ? { ...item, isRead: true } : item,
      );
    },
    /**
     * The inbox was viewed at a location: everything visible there counts as
     * seen. Ids of notices no longer held are dropped.
     */
    markNotificationsSeen: (
      state,
      action: PayloadAction<LocationScopePayload>,
    ) => {
      const seen = new Set(state.seenIds ?? []);
      const visible = state.items.filter(item => inScope(item, action.payload));
      if (visible.every(item => seen.has(item.id))) return;
      state.seenIds = state.items
        .filter(item => seen.has(item.id) || inScope(item, action.payload))
        .map(item => item.id);
    },
    deleteNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
    },
    clearNotifications: (
      state,
      action: PayloadAction<LocationScopePayload>,
    ) => {
      state.items = state.items.filter(item => !inScope(item, action.payload));
    },
    clearReadNotifications: (
      state,
      action: PayloadAction<LocationScopePayload>,
    ) => {
      state.items = state.items.filter(
        item => !item.isRead || !inScope(item, action.payload),
      );
    },
  },
});

export const {
  addNotification,
  replaceServerNotifications,
  markNotificationRead,
  setNotificationReadStatus,
  markAllNotificationsRead,
  markNotificationsSeen,
  deleteNotification,
  clearNotifications,
  clearReadNotifications,
} = notificationsSlice.actions;

type NotificationsState = {
  notifications: NotificationsSliceObject;
  location: { currentLocationId?: string | null };
};

const selectItems = (state: NotificationsState) => state.notifications.items;
const selectSeenIds = (state: NotificationsState) =>
  state.notifications.seenIds;
const selectCurrentLocationId = (state: NotificationsState) =>
  state.location?.currentLocationId ?? null;

/** The notices shown at the current location, newest first. */
export const selectNotifications = createSelector(
  [selectItems, selectCurrentLocationId],
  (items, locationId) =>
    items
      .filter(item => isNotificationVisibleAt(item, locationId))
      .sort(byNewest),
);

export const selectUnreadNotificationsCount = createSelector(
  [selectNotifications],
  items => items.filter(item => !item.isRead).length,
);

/** The bell's count: unread notices not yet shown in the inbox. */
export const selectUnseenNotificationsCount = createSelector(
  [selectNotifications, selectSeenIds],
  (items, seenIds) => {
    const seen = new Set(seenIds ?? []);
    return items.filter(item => !item.isRead && !seen.has(item.id)).length;
  },
);

/**
 * The inbox syncs for the logged-in employee at the current location. Null
 * until both are known: at login the location arrives before the employee.
 */
export const selectNotificationsSyncScope = (state: {
  auth?: { inApp?: { account?: { employeeInfo?: { _id?: unknown } } } };
  location?: { currentLocationId?: string | null };
}): string | null => {
  const employeeId = state.auth?.inApp?.account?.employeeInfo?._id;
  const locationId = state.location?.currentLocationId;
  return employeeId && locationId ? `${employeeId}:${locationId}` : null;
};

export default notificationsSlice.reducer;
